import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, CheckCircle2, XCircle, Loader2, Clock, Trash2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { BackendAPI } from '../lib/api';
import { BatchStatus, FileResult, FileStatusType } from '../types';
import { Button } from './ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import ImportPdfToEsx from './ImportPdfToEsx';

// ================================================================= //
//          Admin Upload Tab                                           //
// ================================================================= //
// Drag-and-drop upload zone with progress tracking, per-file status, //
// and before/after stats diff display.                                //
// ================================================================= //

interface AdminUploadTabProps {
  onProcessingComplete?: () => void;
}

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.pdf'];
const POLL_INTERVAL_MS = 2000;

/**
 * Checks if a file has an accepted extension.
 *
 * Args:
 *   file (File): The file to check.
 *
 * Returns:
 *   boolean: True if the file extension is accepted.
 */
function isAcceptedFile(file: File): boolean {
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  return ACCEPTED_EXTENSIONS.includes(ext);
}

/**
 * Formats a file size in bytes to a human-readable string.
 *
 * Args:
 *   bytes (number): File size in bytes.
 *
 * Returns:
 *   string: Human-readable file size (e.g., "1.2 MB").
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminUploadTab({ onProcessingComplete }: AdminUploadTabProps) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- Drag and Drop handlers ---

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files).filter(isAcceptedFile);
    if (droppedFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...droppedFiles]);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).filter(isAcceptedFile);
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }
    // Reason: Reset input value so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const removeFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // --- Upload and polling ---

  const startPolling = useCallback((id: string) => {
    setPolling(true);
    pollingRef.current = setInterval(async () => {
      try {
        const status = await BackendAPI.getBatchStatus(id);
        setBatchStatus(status);
        if (status.status === 'complete') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPolling(false);
          onProcessingComplete?.();
        }
      } catch (err) {
        // Reason: Don't stop polling on transient errors; let it retry
        console.error('[AdminUploadTab] Polling error:', err);
      }
    }, POLL_INTERVAL_MS);
  }, [onProcessingComplete]);

  const handleUpload = useCallback(async () => {
    if (selectedFiles.length === 0) return;
    setError(null);
    setUploading(true);
    setUploadProgress(0);
    setBatchStatus(null);
    setBatchId(null);

    try {
      const response = await BackendAPI.uploadEstimateFiles(
        selectedFiles,
        (percent) => setUploadProgress(percent)
      );
      setBatchId(response.batch_id);
      setUploading(false);
      setSelectedFiles([]);
      startPolling(response.batch_id);
    } catch (err) {
      setUploading(false);
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  }, [selectedFiles, startPolling]);

  const handleReset = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = null;
    setSelectedFiles([]);
    setUploading(false);
    setUploadProgress(0);
    setBatchId(null);
    setBatchStatus(null);
    setPolling(false);
    setError(null);
  }, []);

  const isProcessing = uploading || polling;
  const isComplete = batchStatus?.status === 'complete';

  return (
    <div className="space-y-6">
      {/* Faithful PDF import -> ESX (Phase 2). Standalone of the batch flow. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Import PDF → ESX</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-muted-foreground">
            Import a carrier PDF estimate and download an importable Xactimate
            .ESX. Faithful replication — codes are matched and quantities
            preserved; unmatched items are flagged for manual entry.
          </p>
          <ImportPdfToEsx />
        </CardContent>
      </Card>

      {/* Drop zone */}
      {!isProcessing && !isComplete && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 transition-colors',
            dragActive
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 hover:border-primary/50 hover:bg-slate-50'
          )}
        >
          <Upload className={cn('h-10 w-10 mb-3', dragActive ? 'text-primary' : 'text-muted-foreground')} />
          <p className="text-sm font-medium text-foreground">Drag files here or click to browse</p>
          <p className="mt-1 text-xs text-muted-foreground">Accepts .xlsx, .xls, and .pdf files</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".xlsx,.xls,.pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Selected files list */}
      {selectedFiles.length > 0 && !isProcessing && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Selected Files ({selectedFiles.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {selectedFiles.map((file, i) => (
              <div key={`${file.name}-${i}`} className="flex items-center justify-between rounded-md border px-3 py-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{file.name}</span>
                  <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
                </div>
                <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <Button onClick={handleUpload} className="mt-4 w-full">
              <Upload className="mr-2 h-4 w-4" />
              Upload & Process
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Error display */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-3 p-4">
            <XCircle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Upload progress */}
      {uploading && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm font-medium">Uploading files...</span>
              <span className="ml-auto text-sm text-muted-foreground">{uploadProgress}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-slate-200">
              <div
                className="h-2 rounded-full bg-primary transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Processing status */}
      {polling && batchStatus && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              Processing Files
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Overall progress */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{batchStatus.processed} / {batchStatus.total_files}</span>
              </div>
              <div className="h-2 w-full rounded-full bg-slate-200">
                <div
                  className="h-2 rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${batchStatus.total_files > 0 ? (batchStatus.processed / batchStatus.total_files) * 100 : 0}%` }}
                />
              </div>
            </div>
            <FileStatusList files={batchStatus.files} />
          </CardContent>
        </Card>
      )}

      {/* Completed status with stats diff */}
      {isComplete && batchStatus && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Processing Complete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 text-sm">
                <span className="text-muted-foreground">
                  {batchStatus.succeeded} succeeded, {batchStatus.failed} failed of {batchStatus.total_files} files
                </span>
              </div>
              <FileStatusList files={batchStatus.files} />
            </CardContent>
          </Card>

          {/* Stats diff */}
          {batchStatus.stats_diff && (
            <StatsDiffCard
              diff={batchStatus.stats_diff}
              after={batchStatus.stats_after}
            />
          )}

          <Button onClick={handleReset} variant="outline" className="w-full">
            Upload More Files
          </Button>
        </div>
      )}
    </div>
  );
}

// ================================================================= //
//          File Status List                                            //
// ================================================================= //

const FileStatusList: React.FC<{ files: FileResult[] }> = ({ files }) => (
  <div className="space-y-1">
    {files.map((file) => (
      <div key={file.filename} className="flex items-center gap-2 rounded-md border px-3 py-2">
        <FileStatusIcon status={file.status} />
        <span className="text-sm font-medium flex-1 truncate">{file.filename}</span>
        {file.status === 'done' && (
          <span className="text-xs text-muted-foreground">
            {file.line_items_parsed} items, {file.rooms_parsed} rooms
          </span>
        )}
        {file.status === 'failed' && file.error && (
          <span className="text-xs text-destructive max-w-xs truncate" title={file.error}>
            {file.error}
          </span>
        )}
      </div>
    ))}
  </div>
);

const FileStatusIcon: React.FC<{ status: FileStatusType }> = ({ status }) => {
  switch (status) {
    case 'queued':
      return <Clock className="h-4 w-4 text-muted-foreground" />;
    case 'processing':
      return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
    case 'done':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-destructive" />;
  }
};

// ================================================================= //
//          Stats Diff Card                                            //
// ================================================================= //

const DIFF_LABELS: Record<string, string> = {
  total_codes: 'Codes',
  total_estimates: 'Estimates',
  co_occurrences: 'Co-occurrence Rules',
  always_write_rules: 'Always-Write Rules',
  quantity_relationships: 'Quantity Relationships',
  room_templates: 'Room Templates',
};

interface StatsDiffCardProps {
  diff: Record<string, number>;
  after: Record<string, number> | null;
}

const StatsDiffCard: React.FC<StatsDiffCardProps> = ({ diff, after }) => {
  const entries = Object.entries(diff).filter(([key]) => DIFF_LABELS[key]);
  const hasChanges = entries.some(([, val]) => val !== 0);

  if (!hasChanges) {
    return (
      <Card className="border-slate-300">
        <CardContent className="p-6 text-center text-sm text-muted-foreground">
          No changes to the knowledge base from this batch.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-200 bg-green-50/50">
      <CardHeader>
        <CardTitle className="text-base text-green-800">Knowledge Base Updated</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {entries.map(([key, val]) => (
            <div key={key}>
              <p className={cn(
                'text-lg font-bold',
                val > 0 ? 'text-green-700' : 'text-muted-foreground'
              )}>
                {val > 0 ? `+${val}` : val}
              </p>
              <p className="text-xs text-muted-foreground">{DIFF_LABELS[key]}</p>
              {after && after[key] !== undefined && (
                <p className="text-xs text-muted-foreground">
                  Total: {typeof after[key] === 'number' ? after[key].toLocaleString() : after[key]}
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
